<template>
  <div class="app-select" :class="rootClasses">
    <label v-if="label" :for="selectId" class="app-select__label">
      {{ label }}
      <span v-if="showRequiredMark" class="app-select__required" aria-hidden="true">*</span>
    </label>

    <div class="app-select__field">
      <span v-if="iconLeft" class="app-select__icon app-select__icon--left">
        <i :class="iconLeft"></i>
      </span>

      <select
        :id="selectId"
        ref="select"
        v-bind="$attrs"
        :value="value"
        :disabled="disabled"
        :required="required"
        :aria-invalid="!!error || null"
        :aria-describedby="describedBy"
        class="app-select__control"
        @change="onChange"
        @blur="onBlur"
        @focus="onFocus"
      >
        <option v-if="emptyOption" value="" :disabled="!clearable && hasValue">
          {{ emptyOptionLabel }}
        </option>

        <template v-if="!isGroupOption">
          <option
            v-for="opt in options"
            :key="String(opt[resolvedValueKey])"
            :value="opt[resolvedValueKey]"
          >
            {{ optionLabel(opt) }}
          </option>
        </template>

        <template v-else>
          <optgroup
            v-for="group in options"
            :key="group.label"
            :label="group.label"
          >
            <option
              v-for="opt in (group.options || [])"
              :key="String(opt[resolvedValueKey])"
              :value="opt[resolvedValueKey]"
            >
              {{ optionLabel(opt) }}
            </option>
          </optgroup>
        </template>
      </select>

      <!-- Custom caret — native one is hidden via appearance: none -->
      <span class="app-select__caret" aria-hidden="true">
        <i class="fa-solid fa-chevron-down"></i>
      </span>
    </div>

    <small v-if="error" :id="errorId" class="app-select__error" v-html="error"></small>
    <small v-else-if="help" :id="helpId" class="app-select__help" v-html="help"></small>
  </div>
</template>

<script>
// Auto-incrementing id for label↔select pairing when caller didn't pass one.
let uid = 0

/**
 * App-styled select — no-Bootstrap, drop-in upgrade over `c-select`.
 *
 * Built on native <select> for free accessibility (keyboard nav, screen
 * readers, mobile system picker). The visible trigger is fully restyled to
 * match `app-input`; the dropdown panel itself is OS-rendered.
 *
 * Migration notes from c-select:
 *   - Same prop names: value, options, valueKey, labelKey, emptyOption,
 *     emptyOptionLabel, label, error, help, requiredMark, isGroupOption.
 *   - Default `labelKey` is `'label'` (modern shape). For `c-select`-style
 *     `{ text, value }` options, either pass `labelKey="text"` or let the
 *     template fall back to `opt.text` automatically.
 *   - Extra knobs: size, variant, iconLeft, disabled, placeholder, clearable.
 *
 * Public methods (via $refs):
 *   - focus()
 */
export default {
  name: 'AppSelect',

  inheritAttrs: false,

  props: {
    value: {
      type: [String, Number, Boolean],
      default: '',
    },
    options: {
      type: Array,
      default: () => [],
    },
    label:    { type: String, default: '' },
    error:    { type: String, default: '' },
    help:     { type: String, default: '' },
    iconLeft: { type: String, default: '' },

    valueKey: { type: String, default: '' },
    labelKey: { type: String, default: '' },

    // Render an empty-string option at the top (e.g. "All …").
    emptyOption: { type: Boolean, default: false },
    emptyOptionLabel: { type: String, default: 'Select…' },
    // If false (default), the empty option becomes non-selectable once any
    // real value is chosen — prevents accidentally clearing via the picker.
    clearable: { type: Boolean, default: false },

    isGroupOption: { type: Boolean, default: false },

    requiredMark: { type: Boolean, default: false },
    required:     { type: Boolean, default: false },
    disabled:     { type: Boolean, default: false },

    size: {
      type: String,
      default: 'md',
      validator: (v) => ['sm', 'md', 'lg'].includes(v),
    },
    variant: {
      type: String,
      default: 'default',
      validator: (v) => ['default', 'ghost'].includes(v),
    },

    id: { type: String, default: '' },
  },

  emits: ['input', 'change', 'blur', 'focus'],

  data() {
    return {
      autoId: `app-select-${++uid}`,
    }
  },

  computed: {
    selectId() { return this.id || this.autoId },
    errorId()  { return `${this.selectId}-error` },
    helpId()   { return `${this.selectId}-help` },

    describedBy() {
      if (this.error) return this.errorId
      if (this.help)  return this.helpId
      return null
    },

    showRequiredMark() {
      return this.requiredMark || this.required
    },

    resolvedValueKey() {
      return this.valueKey || 'value'
    },

    resolvedLabelKey() {
      return this.labelKey || 'label'
    },

    hasValue() {
      return this.value !== '' && this.value != null
    },

    rootClasses() {
      return [
        `app-select--${this.size}`,
        `app-select--${this.variant}`,
        {
          'app-select--has-left-icon':    !!this.iconLeft,
          'app-select--has-error':        !!this.error,
          'app-select--disabled':         this.disabled,
          'app-select--placeholder-shown': !this.hasValue,
        },
      ]
    },
  },

  methods: {
    optionLabel(opt) {
      // Tolerate both modern `{ label, value }` and legacy `{ text, value }`.
      return opt[this.resolvedLabelKey] ?? opt.label ?? opt.text ?? ''
    },

    onChange(e) {
      this.$emit('input', e.target.value)
      this.$emit('change', e.target.value)
    },
    onBlur(e)  { this.$emit('blur', e) },
    onFocus(e) { this.$emit('focus', e) },

    /** Public — programmatically focus the inner select. */
    focus() {
      if (this.$refs.select) this.$refs.select.focus()
    },
  },
}
</script>

<style lang="scss" scoped>
// Local design tokens — match app-input so the two visually rhyme.
$as-text:         #2b3a4a;
$as-text-muted:   #6c7a89;
$as-text-subtle:  #9aa6b3;
$as-bg-surface:   #ffffff;
$as-bg-soft:      #f6f8fa;
$as-border:       rgba(0, 0, 0, 0.10);
$as-blue-focus:   #4392d4;
$as-blue-ring:    rgba(67, 146, 212, 0.16);
$as-red:          #d04848;
$as-red-bg:       rgba(208, 72, 72, 0.08);
$as-red-ring:     rgba(208, 72, 72, 0.18);
$as-radius-md:    8px;
$as-radius-lg:    10px;
$as-duration:     140ms;
$as-ease:         cubic-bezier(.4, 0, .2, 1);

.app-select {
  display: flex;
  flex-direction: column;
  min-width: 0;

  // ── Label ──
  &__label {
    font-size: 11px;
    font-weight: 700;
    color: $as-text-muted;
    text-transform: uppercase;
    letter-spacing: .04em;
    margin-bottom: 6px;
    line-height: 1.2;
  }

  &__required {
    color: $as-red;
    margin-left: 2px;
  }

  // ── Field wrapper ──
  &__field {
    position: relative;
    display: flex;
    align-items: center;
    min-width: 0;
  }

  // ── Control (the actual <select>) ──
  &__control {
    flex: 1;
    width: 100%;
    height: 36px;
    padding: 0 32px 0 12px;  // extra right padding for the caret
    font-size: 13px;
    font-family: inherit;
    color: $as-text;
    background: $as-bg-soft;
    border: 0.5px solid $as-border;
    border-radius: $as-radius-md;
    cursor: pointer;
    transition:
      background  $as-duration $as-ease,
      border-color $as-duration $as-ease,
      box-shadow  $as-duration $as-ease,
      color       $as-duration $as-ease;

    // Strip the browser-default chevron — we render our own.
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    background-image: none;

    // Hide the dotted Firefox focus ring on text — focus shows via our box-shadow.
    &:-moz-focusring {
      color: transparent;
      text-shadow: 0 0 0 $as-text;
    }

    &:hover:not(:disabled) {
      background: lighten($as-bg-soft, 1%);
    }

    &:focus {
      outline: none;
      background: $as-bg-surface;
      border-color: $as-blue-focus;
      box-shadow: 0 0 0 3px $as-blue-ring;
    }

    &:disabled,
    &[disabled] {
      color: $as-text-subtle;
      background: $as-bg-soft;
      cursor: not-allowed;
      opacity: 0.7;
    }
  }

  // ── Placeholder-like color when empty value is selected ──
  &--placeholder-shown &__control {
    color: $as-text-muted;
  }

  // ── Decorative icons ──
  &__icon {
    position: absolute;
    top: 0;
    bottom: 0;
    display: inline-flex;
    align-items: center;
    color: $as-text-subtle;
    font-size: 12px;
    pointer-events: none;

    &--left { left: 12px; }
  }

  // ── Custom caret on the right ──
  &__caret {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    display: inline-flex;
    align-items: center;
    color: $as-text-subtle;
    font-size: 10px;
    pointer-events: none;
    transition: color $as-duration $as-ease;
  }

  // ── Padding compensation for leading icon ──
  &--has-left-icon &__control { padding-left: 34px; }

  // ── Sizes ──
  &--sm &__control {
    height: 28px;
    padding: 0 26px 0 10px;
    font-size: 12px;
  }
  &--sm.app-select--has-left-icon &__control { padding-left: 28px; }
  &--sm &__icon--left { left: 9px; }
  &--sm &__caret     { right: 9px; font-size: 9px; }

  &--lg &__control {
    height: 44px;
    padding: 0 36px 0 14px;
    font-size: 14px;
    border-radius: $as-radius-lg;
  }
  &--lg.app-select--has-left-icon &__control { padding-left: 40px; }
  &--lg &__icon--left { left: 14px; }
  &--lg &__caret     { right: 14px; font-size: 11px; }

  // ── Variants ──
  &--ghost &__control {
    background: transparent;
    border-color: transparent;

    &:hover:not(:disabled) { background: $as-bg-soft; }

    &:focus {
      background: $as-bg-surface;
      border-color: $as-blue-focus;
    }
  }

  // ── Error state ──
  &--has-error &__control {
    border-color: $as-red;
    background: $as-red-bg;

    &:focus { box-shadow: 0 0 0 3px $as-red-ring; }
  }
  &--has-error &__icon,
  &--has-error &__caret { color: $as-red; }

  // ── Disabled state on root ──
  &--disabled {
    pointer-events: none;
    opacity: 0.7;
  }

  // ── Inline messages ──
  &__error,
  &__help {
    font-size: 11px;
    margin-top: 4px;
    line-height: 1.4;
  }

  &__error { color: $as-red; }
  &__help  { color: $as-text-muted; }
}
</style>
